// Order updates (migration 055): what a vendor says about an order, with the same forms in the vendor portal (their
// order's side pane) and on the staff PO panel (HHL entering it for a vendor not on the portal): Confirm, Progress (units
// ready per line; a build's stage and units built), New ship date, Add shipment (what's in it, carrier, tracking,
// boxes), Note. Under them the order's shipments and its timeline, each marked from them or from HHL. Words come from
// `text` (OrderText): the staff app's English, the portal's English and 中文.
import { Loader2, Truck, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { CARRIERS, trackingUrl } from "../../../shared/carriers.ts";
import { MAKE_STAGES, type MakeStage } from "../../../shared/consignment.ts";
import type {
  BuildView,
  ConfirmInput,
  NewDateInput,
  OrderLineView,
  ProgressInput,
  ShipmentInput,
  ShipmentView,
  UpdateView,
} from "../../../shared/order-updates.ts";
import { Button, cx } from "./controls.tsx";
import { type Column, DataTable } from "./DataTable.tsx";

export type OrderText = {
  confirm: string;
  progress: string;
  newDate: string;
  ship: string;
  note: string;
  save: string;
  cancel: string;
  orderRef: string;
  shipDate: string;
  lineByLine: string;
  ready: string;
  builtNow: string;
  built: string;
  stage: string;
  stages: Record<MakeStage, string>;
  reason: string;
  shippedOn: string;
  carrier: string;
  tracking: string;
  boxes: string;
  inIt: string;
  left: (n: string) => string;
  noteLabel: string;
  shipments: string;
  timeline: string;
  noUpdates: string;
  ordered: string;
  price: string;
  shipped: string;
  received: string;
  asked: string;
  says: string;
  inTransit: string;
  receivedOn: (d: string) => string;
  remove: string;
  removeAsk: string;
  kinds: Record<UpdateView["kind"], string>;
  wasDate: (d: string) => string;
  line: (sku: string, what: string) => string;
  good: (n: string) => string;
  damaged: (n: string) => string;
  short: (n: string) => string;
  date: (iso: string | null | undefined) => string;
  error: string;
  /** The tables' headers and counts the words above don't cover (English until a text gives its own). */
  item?: string;
  by?: string;
  lineNoun?: [string, string];
  shipmentNoun?: [string, string];
};

export type OrderView = {
  number: string;
  status: string;
  /** Open to updates: sent and not yet received, closed or cancelled. */
  open: boolean;
  expected_ship: string | null;
  vendor_ship_date: string | null;
  vendor_ref: string;
  lines: OrderLineView[];
  builds: BuildView[];
  shipments: ShipmentView[];
  updates: UpdateView[];
  /** Each line's price as the vendor reads it (the portal; staff see prices on the PO's own lines). */
  priceOf?: (line: OrderLineView) => string;
};

export type OrderActions = {
  confirm: (input: ConfirmInput) => Promise<void>;
  progress: (input: ProgressInput) => Promise<void>;
  newDate: (input: NewDateInput) => Promise<void>;
  ship: (input: ShipmentInput) => Promise<void>;
  note: (note: string) => Promise<void>;
  /** Taking off a shipment entered by mistake (for the ones `canRemove` allows). */
  removeShipment?: (id: string) => Promise<void>;
  canRemove?: (s: ShipmentView) => boolean;
  /** More on a shipment's row (staff: Receive it, for one not received yet). */
  extra?: (s: ShipmentView) => ReactNode;
};

type Form = "confirm" | "progress" | "date" | "ship" | "note";
const n = (v: number) => v.toLocaleString("en-US", { maximumFractionDigits: 3 });
/** Today where the person is (a vendor in China, HHL in Texas), not in UTC. */
const today = () => new Date().toLocaleDateString("en-CA");
const box = "h-7 w-full min-w-0 rounded-md border border-line bg-surface px-2 text-[12px] outline-none focus:border-accent";
const label = "mb-0.5 block text-[11px] text-muted";
/** A number box's value: "" is none; anything else must be a number of 0 or more. */
const num = (s: string) => {
  if (!s.trim()) return null;
  const v = Number(s.replace(/[,\s]/g, ""));
  if (!Number.isFinite(v) || v < 0) throw new Error("bad");
  return v;
};

export function OrderUpdates({ order, actions, text: t, onError }: { order: OrderView; actions: OrderActions; text: OrderText; onError: (m: string) => void }) {
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      setForm(null);
    } catch (e) {
      onError((e as Error).message === "bad" ? t.error : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const buttons: [Form, string][] = [
    ...(order.status === "sent" ? [["confirm", t.confirm] as [Form, string]] : []),
    ["progress", t.progress],
    ["date", t.newDate],
    ["ship", t.ship],
    ["note", t.note],
  ];
  return (
    <div className="space-y-4 text-[12px]">
      <Lines order={order} t={t} />
      {order.open && (
        <div className="flex flex-wrap gap-1.5">
          {buttons.map(([key, title]) => (
            <Button
              key={key}
              size="sm"
              variant={form === key ? "primary" : key === "confirm" ? "primary" : "secondary"}
              onClick={() => setForm(form === key ? null : key)}
            >
              {title}
            </Button>
          ))}
        </div>
      )}
      {form === "confirm" && <ConfirmForm order={order} t={t} busy={busy} cancel={() => setForm(null)} submit={(i) => run(() => actions.confirm(i))} />}
      {form === "progress" && <ProgressForm order={order} t={t} busy={busy} cancel={() => setForm(null)} submit={(i) => run(() => actions.progress(i))} />}
      {form === "date" && <DateForm order={order} t={t} busy={busy} cancel={() => setForm(null)} submit={(i) => run(() => actions.newDate(i))} />}
      {form === "ship" && <ShipForm order={order} t={t} busy={busy} cancel={() => setForm(null)} submit={(i) => run(() => actions.ship(i))} />}
      {form === "note" && <NoteForm t={t} busy={busy} cancel={() => setForm(null)} submit={(note) => run(() => actions.note(note))} />}
      {order.shipments.length > 0 && <Shipments order={order} actions={actions} t={t} onError={onError} />}
      <Timeline order={order} t={t} />
    </div>
  );
}

type LineRow = OrderLineView & { position: number; build: BuildView | undefined };

/** The lines with what's ordered, ready, shipped and received, and the dates asked for and said. */
function Lines({ order, t }: { order: OrderView; t: OrderText }) {
  const asked = order.expected_ship;
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- the table's View and Columns
  const rows: LineRow[] = order.lines.map((l, i) => ({ ...l, position: i + 1, build: order.builds.find((b) => b.po_line_id === l.id) }));
  const stageOf = (b: BuildView) => `${t.stages[b.stage as MakeStage] ?? b.stage} · ${t.built} ${n(b.made)}`;
  const count = (v: number) => (v ? n(v) : "");
  const columns: Column<LineRow>[] = [
    { key: "sku", header: "SKU", width: "minmax(110px,1fr)", cell: (l) => <span className="truncate font-mono">{l.sku}</span> },
    {
      key: "name",
      header: t.item ?? "Item",
      width: "minmax(140px,1.5fr)",
      value: (l) => `${l.vendor_sku ? `${l.vendor_sku} · ` : ""}${l.name}`,
      cell: (l) => (
        <span className="truncate text-muted" title={l.name}>
          {l.vendor_sku ? `${l.vendor_sku} · ` : ""}
          {l.name}
        </span>
      ),
    },
    ...(order.builds.length
      ? [
          {
            key: "stage",
            header: t.stage,
            width: "150px",
            value: (l: LineRow) => (l.build ? stageOf(l.build) : ""),
            cell: (l: LineRow) => <span className="truncate text-muted">{l.build ? stageOf(l.build) : ""}</span>,
          },
        ]
      : []),
    { key: "qty", header: t.ordered, width: "84px", align: "right", cell: (l) => n(l.qty) },
    ...(order.priceOf ? [{ key: "price", header: t.price, width: "100px", align: "right" as const, value: (l: LineRow) => order.priceOf?.(l) ?? "" }] : []),
    { key: "qty_ready", header: t.ready, width: "84px", align: "right", cell: (l) => count(l.qty_ready) },
    { key: "qty_shipped", header: t.shipped, width: "84px", align: "right", cell: (l) => count(l.qty_shipped) },
    { key: "qty_received", header: t.received, width: "84px", align: "right", cell: (l) => count(l.qty_received) },
    {
      key: "vendor_date",
      header: t.says,
      width: "110px",
      value: (l) => l.vendor_date ?? "",
      text: (l) => t.date(l.vendor_date),
      cell: (l) => t.date(l.vendor_date),
    },
    // The order the lines are on the order in (the table's own order)
    { key: "position", header: "#", width: "48px", align: "right", optional: true, cell: (l) => <span className="text-faint">{l.position}</span> },
  ];
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        <span>
          <span className="text-muted">{t.asked}</span> {t.date(asked) || "—"}
        </span>
        <span>
          <span className="text-muted">{t.says}</span>{" "}
          <span className={cx(order.vendor_ship_date && asked && order.vendor_ship_date > asked && "font-medium text-warn")}>
            {t.date(order.vendor_ship_date) || "—"}
          </span>
        </span>
        {order.vendor_ref && (
          <span>
            <span className="text-muted">{t.orderRef}</span> {order.vendor_ref}
          </span>
        )}
        <div ref={setSlot} className="ml-auto flex items-center gap-1" />
      </div>
      <DataTable
        fit
        rows={rows}
        columns={columns}
        defaultSort={{ key: "position" }}
        exportName="Order lines"
        viewsSlot={slot}
        pageFilters={false}
        rowNoun={t.lineNoun ?? ["line", "lines"]}
      />
    </div>
  );
}

function Frame({
  title,
  busy,
  cancel,
  submit,
  t,
  children,
}: {
  title: string;
  busy: boolean;
  cancel: () => void;
  submit: () => void;
  t: OrderText;
  children: ReactNode;
}) {
  const [error, setError] = useState("");
  return (
    <form
      className="space-y-2 rounded-md border border-accent/50 bg-surface p-2.5"
      onSubmit={(e) => {
        e.preventDefault();
        setError("");
        try {
          submit();
        } catch {
          setError(t.error);
        }
      }}
    >
      <div className="font-medium">{title}</div>
      {children}
      {error && <div className="text-danger">{error}</div>}
      <div className="flex gap-1.5 pt-1">
        <Button size="sm" variant="primary" type="submit" disabled={busy} icon={busy ? <Loader2 className="size-3.5 animate-spin" /> : undefined}>
          {t.save}
        </Button>
        <Button size="sm" variant="ghost" onClick={cancel}>
          {t.cancel}
        </Button>
      </div>
    </form>
  );
}

const Field = ({ title, children, className }: { title: string; children: ReactNode; className?: string }) => (
  <label className={cx("block", className)}>
    <span className={label}>{title}</span>
    {children}
  </label>
);

type FormProps<I> = { order: OrderView; t: OrderText; busy: boolean; cancel: () => void; submit: (input: I) => void };

function ConfirmForm({ order, t, busy, cancel, submit }: FormProps<ConfirmInput>) {
  const [ref, setRef] = useState(order.vendor_ref);
  const [date, setDate] = useState(order.vendor_ship_date ?? order.expected_ship ?? "");
  const [each, setEach] = useState(false);
  const [dates, setDates] = useState<Record<string, string>>(Object.fromEntries(order.lines.map((l) => [l.id, l.vendor_date ?? ""])));
  const [note, setNote] = useState("");
  return (
    <Frame
      title={t.confirm}
      busy={busy}
      cancel={cancel}
      t={t}
      submit={() =>
        submit({
          vendor_ref: ref.trim(),
          vendor_ship_date: date || null,
          ...(each ? { lines: order.lines.map((l) => ({ po_line_id: l.id, vendor_date: dates[l.id] || null })) } : {}),
          note: note.trim(),
        })
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <Field title={t.orderRef}>
          <input value={ref} maxLength={100} onChange={(e) => setRef(e.target.value)} className={box} />
        </Field>
        <Field title={t.shipDate}>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={box} />
        </Field>
      </div>
      <label className="flex items-center gap-1.5">
        <input type="checkbox" checked={each} onChange={(e) => setEach(e.target.checked)} />
        {t.lineByLine}
      </label>
      {each &&
        order.lines.map((l) => (
          <div key={l.id} className="grid grid-cols-[minmax(0,1fr)_9rem] items-center gap-2">
            <span className="truncate font-mono">{l.sku}</span>
            <input type="date" value={dates[l.id]} onChange={(e) => setDates({ ...dates, [l.id]: e.target.value })} className={box} />
          </div>
        ))}
      <Field title={t.noteLabel}>
        <textarea rows={2} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} className={cx(box, "h-auto py-1")} />
      </Field>
    </Frame>
  );
}

function ProgressForm({ order, t, busy, cancel, submit }: FormProps<ProgressInput>) {
  const [ready, setReady] = useState<Record<string, string>>(Object.fromEntries(order.lines.map((l) => [l.id, l.qty_ready ? String(l.qty_ready) : ""])));
  const [stages, setStages] = useState<Record<string, string>>(Object.fromEntries(order.builds.map((b) => [b.id, b.stage])));
  const [made, setMade] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const buildOf = (lineId: string) => order.builds.find((b) => b.po_line_id === lineId);
  return (
    <Frame
      title={t.progress}
      busy={busy}
      cancel={cancel}
      t={t}
      submit={() =>
        submit({
          in_production: true,
          lines: order.lines
            .filter((l) => !buildOf(l.id) && (num(ready[l.id] ?? "") ?? 0) !== l.qty_ready)
            .map((l) => ({ po_line_id: l.id, qty_ready: num(ready[l.id] ?? "") ?? 0 })),
          builds: order.builds
            .map((b) => ({
              make_order_id: b.id,
              made: num(made[b.id] ?? "") ?? undefined,
              stage: stages[b.id] !== b.stage ? (stages[b.id] as MakeStage) : undefined,
            }))
            .filter((b) => b.made || b.stage),
          note: note.trim(),
        })
      }
    >
      {order.lines.map((l) => {
        const b = buildOf(l.id);
        return (
          <div key={l.id} className="grid grid-cols-[minmax(0,1fr)_7rem_8rem] items-end gap-2">
            <div className="min-w-0 pb-1.5">
              <div className="truncate font-mono">{l.sku}</div>
              <div className="text-muted">
                {t.ordered} {n(l.qty)}
                {b && ` · ${t.built} ${n(b.made)}`}
              </div>
            </div>
            {b ? (
              <>
                <Field title={t.builtNow}>
                  <input inputMode="decimal" value={made[b.id] ?? ""} onChange={(e) => setMade({ ...made, [b.id]: e.target.value })} className={box} />
                </Field>
                <Field title={t.stage}>
                  <select value={stages[b.id]} onChange={(e) => setStages({ ...stages, [b.id]: e.target.value })} className={box}>
                    {MAKE_STAGES.map((s) => (
                      <option key={s} value={s}>
                        {t.stages[s]}
                      </option>
                    ))}
                  </select>
                </Field>
              </>
            ) : (
              <Field title={t.ready} className="col-span-2 max-w-28">
                <input inputMode="decimal" value={ready[l.id] ?? ""} onChange={(e) => setReady({ ...ready, [l.id]: e.target.value })} className={box} />
              </Field>
            )}
          </div>
        );
      })}
      <Field title={t.noteLabel}>
        <textarea rows={2} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} className={cx(box, "h-auto py-1")} />
      </Field>
    </Frame>
  );
}

function DateForm({ order, t, busy, cancel, submit }: FormProps<NewDateInput>) {
  const [date, setDate] = useState(order.vendor_ship_date ?? order.expected_ship ?? "");
  const [why, setWhy] = useState("");
  return (
    <Frame title={t.newDate} busy={busy} cancel={cancel} t={t} submit={() => submit({ vendor_ship_date: date, note: why.trim() })}>
      <Field title={t.shipDate} className="w-40">
        <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className={box} />
      </Field>
      <Field title={t.reason}>
        <textarea required rows={2} value={why} maxLength={2000} onChange={(e) => setWhy(e.target.value)} className={cx(box, "h-auto py-1")} />
      </Field>
    </Frame>
  );
}

function ShipForm({ order, t, busy, cancel, submit }: FormProps<ShipmentInput>) {
  const left = (l: OrderLineView) => Math.max(0, l.qty - l.qty_shipped);
  const [qty, setQty] = useState<Record<string, string>>(Object.fromEntries(order.lines.map((l) => [l.id, left(l) ? String(left(l)) : ""])));
  const [on, setOn] = useState(today());
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [boxes, setBoxes] = useState("");
  const [note, setNote] = useState("");
  return (
    <Frame
      title={t.ship}
      busy={busy}
      cancel={cancel}
      t={t}
      submit={() =>
        submit({
          shipped_on: on,
          carrier: carrier.trim(),
          tracking_number: tracking.trim(),
          boxes: boxes.trim() ? Math.round(num(boxes) ?? 0) || null : null,
          note: note.trim(),
          lines: order.lines.map((l) => ({ po_line_id: l.id, qty: num(qty[l.id] ?? "") ?? 0 })).filter((l) => l.qty > 0),
        })
      }
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Field title={t.shippedOn}>
          <input type="date" required value={on} onChange={(e) => setOn(e.target.value)} className={box} />
        </Field>
        <Field title={t.carrier}>
          <input list="order-update-carriers" value={carrier} maxLength={50} onChange={(e) => setCarrier(e.target.value)} className={box} />
          <datalist id="order-update-carriers">
            {CARRIERS.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
        <Field title={t.tracking}>
          <input value={tracking} maxLength={100} onChange={(e) => setTracking(e.target.value)} className={box} />
        </Field>
        <Field title={t.boxes}>
          <input inputMode="numeric" value={boxes} onChange={(e) => setBoxes(e.target.value)} className={box} />
        </Field>
      </div>
      <div className={label}>{t.inIt}</div>
      {order.lines.map((l) => (
        <div key={l.id} className="grid grid-cols-[minmax(0,1fr)_7rem] items-center gap-2">
          <span className="min-w-0 truncate">
            <span className="font-mono">{l.sku}</span> <span className="text-muted">{t.left(n(left(l)))}</span>
          </span>
          <input inputMode="decimal" value={qty[l.id] ?? ""} onChange={(e) => setQty({ ...qty, [l.id]: e.target.value })} className={box} />
        </div>
      ))}
      <Field title={t.noteLabel}>
        <textarea rows={2} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} className={cx(box, "h-auto py-1")} />
      </Field>
    </Frame>
  );
}

function NoteForm({ t, busy, cancel, submit }: Omit<FormProps<string>, "order">) {
  const [note, setNote] = useState("");
  return (
    <Frame title={t.note} busy={busy} cancel={cancel} t={t} submit={() => note.trim() && submit(note.trim())}>
      <textarea required rows={3} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} className={cx(box, "h-auto py-1")} />
    </Frame>
  );
}

function Shipments({ order, actions, t, onError }: { order: OrderView; actions: OrderActions; t: OrderText; onError: (m: string) => void }) {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- the table's View and Columns
  const status = (s: ShipmentView) => (s.received_at ? t.receivedOn(t.date(s.received_at)) : t.inTransit);
  const inIt = (s: ShipmentView) => s.lines.map((l) => `${l.sku} × ${n(l.qty)}`).join(" · ");
  const columns: Column<ShipmentView>[] = [
    {
      key: "shipped_on",
      header: t.shippedOn,
      width: "110px",
      text: (s) => t.date(s.shipped_on),
      cell: (s) => (
        <span className="flex items-center gap-1.5 font-medium">
          <Truck className="size-3.5 shrink-0 text-muted" />
          {t.date(s.shipped_on)}
        </span>
      ),
    },
    { key: "carrier", header: t.carrier, width: "110px", cell: (s) => <span className="truncate">{s.carrier}</span> },
    {
      key: "tracking_number",
      header: t.tracking,
      width: "minmax(130px,1fr)",
      cell: (s) => {
        const url = s.tracking_number ? trackingUrl(s.carrier, s.tracking_number) : null;
        return url ? (
          <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="truncate font-mono text-accent-text hover:underline">
            {s.tracking_number}
          </a>
        ) : (
          <span className="truncate font-mono">{s.tracking_number}</span>
        );
      },
    },
    { key: "boxes", header: t.boxes, width: "70px", align: "right", value: (s) => s.boxes ?? "" },
    {
      key: "status",
      header: t.received,
      width: "130px",
      value: (s) => s.received_at ?? "",
      text: status,
      cell: (s) => <span className={cx("truncate", s.received_at ? "text-ok" : "text-muted")}>{status(s)}</span>,
    },
    {
      key: "lines",
      header: t.inIt,
      width: "minmax(160px,2fr)",
      value: inIt,
      cell: (s) => (
        <span className="truncate text-muted" title={inIt(s)}>
          {inIt(s)}
        </span>
      ),
    },
    {
      key: "note",
      header: t.kinds.note,
      width: "minmax(120px,1fr)",
      cell: (s) =>
        s.note && (
          <span className="truncate text-muted" title={s.note}>
            “{s.note}”
          </span>
        ),
    },
    { key: "by_name", header: t.by ?? "By", width: "120px", cell: (s) => <span className="truncate text-muted">{s.by_name}</span> },
    {
      key: "actions",
      header: "",
      width: "110px",
      value: () => "",
      cell: (s) => (
        <span className="ml-auto flex items-center gap-2">
          {actions.extra?.(s)}
          {actions.removeShipment && actions.canRemove?.(s) && !s.received_at && (
            <button
              type="button"
              title={t.remove}
              aria-label={t.remove}
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(t.removeAsk)) actions.removeShipment?.(s.id).catch((err) => onError((err as Error).message));
              }}
              className="rounded p-0.5 text-faint hover:bg-hover hover:text-danger"
            >
              <X className="size-3.5" />
            </button>
          )}
        </span>
      ),
    },
  ];
  return (
    <section>
      <div className="mb-1 flex items-center gap-2">
        <h4 className="text-[11px] font-medium tracking-wide text-faint uppercase">{t.shipments}</h4>
        <div ref={setSlot} className="ml-auto flex items-center gap-1" />
      </div>
      <DataTable
        fit
        rows={order.shipments}
        columns={columns}
        defaultSort={{ key: "shipped_on", desc: true }}
        exportName="Order shipments"
        viewsSlot={slot}
        pageFilters={false}
        rowNoun={t.shipmentNoun ?? ["shipment", "shipments"]}
      />
    </section>
  );
}

/** One update on the timeline, in words. */
function describe(u: UpdateView, t: OrderText): string {
  const d = u.detail as Record<string, unknown>;
  const lines = (Array.isArray(d.lines) ? d.lines : []) as Record<string, unknown>[];
  if (u.kind === "confirmed")
    return [
      d.vendor_ref ? `${t.orderRef} ${d.vendor_ref}` : "",
      u.new_date ? `${t.says} ${t.date(u.new_date)}` : "",
      ...lines.map((l) => t.line(String(l.sku), t.date(l.vendor_date as string))),
    ]
      .filter(Boolean)
      .join(" · ");
  if (u.kind === "progress") {
    const builds = (Array.isArray(d.builds) ? d.builds : []) as Record<string, unknown>[];
    return [
      ...lines.map((l) => t.line(String(l.sku), `${t.ready} ${n(Number(l.qty_ready))}`)),
      ...builds.map((b) =>
        t.line(
          String(b.sku),
          [b.made ? `${t.builtNow} ${n(Number(b.made))}` : "", b.stage ? (t.stages[b.stage as MakeStage] ?? String(b.stage)) : ""].filter(Boolean).join(", "),
        ),
      ),
    ].join(" · ");
  }
  if (u.kind === "new_date") return `${t.date(u.new_date)}${d.was ? ` (${t.wasDate(t.date(d.was as string))})` : ""}`;
  if (u.kind === "shipped")
    return [String(d.carrier ?? ""), String(d.tracking_number ?? ""), ...lines.map((l) => `${l.sku} × ${n(Number(l.qty))}`)].filter(Boolean).join(" · ");
  if (u.kind === "received")
    return lines
      .map((l) =>
        t.line(
          String(l.sku),
          [t.good(n(Number(l.good))), Number(l.damaged) ? t.damaged(n(Number(l.damaged))) : "", Number(l.short) ? t.short(n(Number(l.short))) : ""]
            .filter(Boolean)
            .join(", "),
        ),
      )
      .join(" · ");
  return "";
}

function Timeline({ order, t }: { order: OrderView; t: OrderText }) {
  return (
    <section>
      <h4 className="mb-1 text-[11px] font-medium tracking-wide text-faint uppercase">{t.timeline}</h4>
      {!order.updates.length && <p className="text-muted">{t.noUpdates}</p>}
      <ul className="space-y-1.5">
        {order.updates.map((u) => {
          const what = describe(u, t);
          const late = u.kind === "new_date";
          const problem =
            u.kind === "received" && ((u.detail as { lines?: { damaged?: number; short?: number }[] }).lines ?? []).some((l) => l.damaged || l.short);
          return (
            <li key={u.id} className="flex gap-2">
              <span className="w-20 shrink-0 text-faint">{t.date(u.created_at)}</span>
              <span className="min-w-0 flex-1">
                <span className={cx("font-medium", (late || problem) && "text-warn")}>{t.kinds[u.kind]}</span>
                {what && <span> · {what}</span>}
                {u.note && <span className="text-muted"> · “{u.note}”</span>}
                <span className="text-faint"> · {u.by_name}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The staff app's words (the portal has its own, in English and 中文). */
export const ORDER_TEXT_STAFF: OrderText = {
  confirm: "Confirmed by them",
  progress: "Progress",
  newDate: "New ship date",
  ship: "Add shipment",
  note: "Add a note",
  save: "Save",
  cancel: "Cancel",
  orderRef: "Their order no.",
  shipDate: "Ships on",
  lineByLine: "A date for each line",
  ready: "Ready",
  builtNow: "Built now",
  built: "built",
  stage: "Stage",
  stages: { planned: "Planned", materials_ready: "Materials ready", smt: "SMT", test: "Test", assembly: "Assembly", packing: "Packing", shipped: "Shipped" },
  reason: "Why it changed",
  shippedOn: "Shipped on",
  carrier: "Carrier",
  tracking: "Tracking number",
  boxes: "Boxes",
  inIt: "What's in it",
  left: (x) => `(${x} left)`,
  noteLabel: "Note (optional)",
  shipments: "Shipments",
  timeline: "Updates",
  noUpdates: "Nothing yet. Enter what the vendor says here when they aren't on the portal.",
  ordered: "Ordered",
  price: "Price",
  shipped: "Shipped",
  received: "Received",
  asked: "We asked for",
  says: "They say",
  inTransit: "On its way",
  receivedOn: (d) => `Received ${d}`,
  remove: "Take this shipment off",
  removeAsk: "Take this shipment off? Its quantities come off the lines again.",
  kinds: { confirmed: "Confirmed", progress: "Progress", new_date: "New ship date", shipped: "Shipped", received: "Received at the CM", note: "Note" },
  wasDate: (d) => `was ${d}`,
  line: (sku, what) => `${sku}: ${what}`,
  good: (x) => `${x} good`,
  damaged: (x) => `${x} damaged`,
  short: (x) => `${x} short`,
  date: (iso) => {
    if (!iso) return "";
    const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }) });
  },
  error: "Check the numbers: each is 0 or more",
};
